import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Default "sm" preserves the previous max-w-lg footprint for confirms /
   *  small forms. "lg" goes to max-w-5xl for richer pickers. "xl" stretches
   *  to ~90vw / 90vh with an inner scroll area for fullscreen previews. */
  size?: "sm" | "lg" | "xl";
}

export function Modal({ isOpen, onClose, title, children, size = "sm" }: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClass =
    size === "xl"
      ? "w-[90vw] max-w-[1400px] h-[90vh] max-h-[90vh]"
      : size === "lg"
      ? "w-full max-w-5xl mx-4"
      : "w-full max-w-lg mx-4";

  // xl uses an internal scrolling body so the modal itself stays fixed-size.
  const bodyClass = size === "xl" ? "px-6 py-4 flex-1 min-h-0 overflow-y-auto" : "px-6 py-4";
  const containerClass = size === "xl" ? "flex flex-col" : "";

  // Portal to document.body so the modal escapes any ancestor stacking context
  // — important for the XL preview which is nested inside a sticky aside on
  // the branding form. Without the portal, transform/contain on an ancestor
  // can pin the "fixed" backdrop inside the parent's box, causing the sidebar
  // (z-40) to appear over the modal.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal content */}
      <div className={`relative z-10 bg-white rounded-xl shadow-xl ${sizeClass} ${containerClass}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={bodyClass}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
